import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Globe, MessageSquare, Settings, Sparkles } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'setting-language', title: 'Setting your preferred communication language' },
  { id: 'how-it-works', title: 'How it works' },
  { id: 'supported-languages', title: 'Supported languages' },
  { id: 'tips', title: 'Tips for best results' },
];

const supportedLanguages = [
  { name: 'English', native: 'English', flag: '🇺🇸' },
  { name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  { name: 'French', native: 'Français', flag: '🇫🇷' },
  { name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  { name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  { name: 'Portuguese', native: 'Português', flag: '🇵🇹' },
  { name: 'Dutch', native: 'Nederlands', flag: '🇳🇱' },
  { name: 'Russian', native: 'Русский', flag: '🇷🇺' },
  { name: 'Chinese', native: '中文', flag: '🇨🇳' },
  { name: 'Japanese', native: '日本語', flag: '🇯🇵' },
  { name: 'Korean', native: '한국어', flag: '🇰🇷' },
  { name: 'Arabic', native: 'العربية', flag: '🇸🇦' },
  { name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
  { name: 'Bengali', native: 'বাংলা', flag: '🇧🇩' },
  { name: 'Turkish', native: 'Türkçe', flag: '🇹🇷' },
  { name: 'Vietnamese', native: 'Tiếng Việt', flag: '🇻🇳' },
  { name: 'Thai', native: 'ไทย', flag: '🇹🇭' },
  { name: 'Indonesian', native: 'Bahasa Indonesia', flag: '🇮🇩' },
  { name: 'Malay', native: 'Bahasa Melayu', flag: '🇲🇾' },
  { name: 'Polish', native: 'Polski', flag: '🇵🇱' },
  { name: 'Ukrainian', native: 'Українська', flag: '🇺🇦' },
  { name: 'Czech', native: 'Čeština', flag: '🇨🇿' },
  { name: 'Swedish', native: 'Svenska', flag: '🇸🇪' },
  { name: 'Danish', native: 'Dansk', flag: '🇩🇰' },
  { name: 'Norwegian', native: 'Norsk', flag: '🇳🇴' },
  { name: 'Finnish', native: 'Suomi', flag: '🇫🇮' },
  { name: 'Greek', native: 'Ελληνικά', flag: '🇬🇷' },
  { name: 'Hebrew', native: 'עברית', flag: '🇮🇱' },
  { name: 'Romanian', native: 'Română', flag: '🇷🇴' },
  { name: 'Hungarian', native: 'Magyar', flag: '🇭🇺' },
  { name: 'Catalan', native: 'Català', flag: '🇪🇸' },
  { name: 'Croatian', native: 'Hrvatski', flag: '🇭🇷' },
  { name: 'Slovak', native: 'Slovenčina', flag: '🇸🇰' },
  { name: 'Bulgarian', native: 'Български', flag: '🇧🇬' },
  { name: 'Serbian', native: 'Српски', flag: '🇷🇸' },
  { name: 'Bosnian', native: 'Bosanski', flag: '🇧🇦' },
  { name: 'Welsh', native: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  { name: 'Cebuano', native: 'Cebuano', flag: '🇵🇭' },
  { name: 'Filipino', native: 'Filipino', flag: '🇵🇭' },
  { name: 'Dzongkha', native: 'རྫོང་ཁ', flag: '🇧🇹' },
];

const relatedArticles = [
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-web-app' },
  { title: 'What languages is the UI available in?', href: '/doc/article/ui-languages' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
];

export default function MultiLanguageSupport() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Multi-Language Support in SweetPilot - AI Communication in 40+ Languages',
    description: 'Learn about SweetPilot multi-language support. AI employees can communicate in over 40 languages with automatic detection and seamless switching.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/multi-language-support' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Multi-Language Support - AI Communication in 40+ Languages | SweetPilot Help Center</title>
        <meta name="description" content="SweetPilot AI employees communicate in over 40 languages with automatic detection. Set your preferred language and get seamless multilingual support." />
        <meta name="keywords" content="SweetPilot multi-language, AI multilingual, language support, AI translation, international AI, global AI assistant, language detection" />
        <meta property="og:title" content="SweetPilot Multi-Language Support - 40+ Languages" />
        <meta property="og:description" content="AI employees that communicate in your preferred language with automatic detection." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/multi-language-support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Multi-Language AI" />
        <meta name="twitter:description" content="Communicate with AI employees in 40+ languages." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/multi-language-support" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            <li>
              <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                All Collections
              </Link>
            </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              Getting started
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              General info
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Multiple language support in SweetPilot App
          </li>
        </ol>
      </nav>

      <div className="flex gap-10">
        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Multiple language support in SweetPilot App
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn how to set your preferred language in SweetPilot App and communicate with AI employees in over 100 languages.
            </p>
            <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <span className="text-sm">S</span>
              </div>
              <div>
                <span>Written by </span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
              </div>
              <span>•</span>
              <span>Updated recently</span>
            </div>
          </motion.div>

          {/* Overview Section */}
          <motion.section
            id="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            <p className={`text-lg leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot App supports more than 100 languages. This means that you can send messages to AI employees and receive their responses in the language of your choice. This also applies to power-up instructions and external-platform tasks. For example, if you provide instructions in Spanish, the AI employee will complete the task for you in Spanish.
            </p>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The platform's default interface (UI) language is English and Spanish is now also available as an additional interface option.
            </p>
          </motion.section>

          {/* Setting Language Section */}
          <motion.section
            id="setting-language"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Setting your preferred communication language
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              You can set your preferred language from the list in Account Settings:
            </p>
            <ol className={`list-decimal list-inside space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                Go to{' '}
                <Link
                  to="/dashboard/settings"
                  className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  Account Settings
                </Link>.
              </li>
              <li>Select your preferred language from the list.</li>
            </ol>

            <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              By following these steps, you can ensure that all interactions on SweetPilot App are in your chosen language.
            </p>
          </motion.section>

          {/* How It Works Section */}
          <motion.section
            id="how-it-works"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How it works
            </h2>

            <div className="grid gap-4">
              <div className={`p-5 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <MessageSquare className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Automatic Language Detection
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      When you write to an AI employee in any language, they automatically detect and respond in the same language. No additional configuration needed.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-5 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <Settings className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Consistent Experience
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      Once you set your preferred language, all AI employees will communicate with you in that language by default across all conversations and tasks.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-5 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <Sparkles className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Task Execution in Your Language
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      When AI employees perform tasks on external platforms (like creating social media posts), they will use your preferred language for the content they generate.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Supported Languages Section */}
          <motion.section
            id="supported-languages"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Supported languages
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot AI employees can communicate in over 100 languages. Here are some of the most popular options:
            </p>

            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                {supportedLanguages.map((lang, index) => (
                  <div
                    key={lang.name}
                    className={`p-3 flex items-center gap-2 ${
                      index < supportedLanguages.length - (supportedLanguages.length % 4 || 4)
                        ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                        : ''
                    } ${
                      (index + 1) % 4 !== 0
                        ? isDark ? 'border-r border-white/5' : 'border-r border-slate-100'
                        : ''
                    }`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <div className="min-w-0">
                      <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {lang.name}
                      </p>
                      <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        {lang.native}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              And many more! Our AI employees leverage advanced language models that support virtually any language you need.
            </p>
          </motion.section>

          {/* Tips Section */}
          <motion.section
            id="tips"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Tips for best results
            </h2>

            <div className={`p-5 rounded-xl border ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
              <div className="flex items-start gap-3">
                <Globe className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <div className={`space-y-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  <p>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Be consistent:</strong> For best results, stick to one language per conversation. Mixing languages may occasionally cause confusion.
                  </p>
                  <p>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Set your preference:</strong> If you always want responses in a specific language, set it in your account settings for a seamless experience.
                  </p>
                  <p>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Specify when needed:</strong> If you need content in a different language than your default, simply mention it in your request (e.g., "Write this blog post in French").
                  </p>
                  <p>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Regional variations:</strong> You can specify regional language variations like "Brazilian Portuguese" or "Latin American Spanish" for more targeted content.
                  </p>
                </div>
              </div>
            </div>

            <p className={`mt-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Feel free to explore the language settings and make the most out of your interactions on SweetPilot App!
            </p>
          </motion.section>

          {/* Divider */}
          <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

          {/* Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-10"
          >
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={article.title}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1
                      ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                      : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Did this answer your question?
            </p>
            <div className="flex justify-center gap-4">
              <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                😞
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                😐
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                😃
              </button>
            </div>
          </motion.div>
        </div>

        {/* Sidebar TOC */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    activeSection === section.id
                      ? isDark
                        ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                        : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
      </StaticPageLayout>
    </>
  );
}
